import type { ReactNode } from 'react';

import type { BackgroundColor } from '../../constants';
import type { MotifScheme } from '../../motif';
import type { BaseComponentProps } from '../../types';
import type { IconName } from '../Icon';

export type GlobalHeaderNavItemAlignment = 'left' | 'right' | 'center';
export interface GlobalHeaderProps extends BaseComponentProps {
  logo: ReactNode;
  siteName?: ReactNode;
  trackingSiteName?: string;
  defaultGroupKey?: string;
  motifScheme?: MotifScheme;
  /** @deprecated Use motifScheme instead. */
  backgroundColor?: BackgroundColor;
  cta?: ReactNode;
  localNavDesktop?: ReactNode;
  children?: ReactNode;
  displayed?: boolean;
  onToggleExpanded?: (isExpanded: boolean) => void;
  showNavScreen?: boolean;
  endChildrenClassName?: string;
  /** Whether to display the waffle icon on desktop and have global links in mobile at all. */
  showGlobalLinks?: boolean;

  /**
   * Variable that must remain the same for the nav to stay open.
   *
   * If this "invariant" changes, the nav closes. Canonical example of this is the current URL path.
   */
  stayOpenInvariant?: string;

  /**
   * Checks whether a given URL is the same as the current window URL. Used in determining
   * navigation breadcrumbs.
   */
  isUrlCurrent?: (url: string) => boolean;

  /** Feauter flag tested alignment of nav items on desktop */
  navItemAlignment?: GlobalHeaderNavItemAlignment;

  /** Aria label for the menu toggle button */
  buttonAriaLabel?: string;

  /** Custom icon for the header to replace the waffle icon */
  customIcon?: IconName;

  /**
   * Custom styling for snap.com homepage controlled via prop Less margins and adds a black border.
   *
   * This is NOT the ideal way to implement this, but is a quick solve due to the fast deadling for
   * the snap.com homepage update.
   *
   * Unfortunately, due to how the DOM is structured, it is not possible to use a classname to
   * implement everything like we did in footer.
   */
  lessMargins?: boolean;

  /** Adds a black border to the bottom of the header. */
  addBottomBorder?: boolean;

  /** Adds custom styling for the snap.com homepage. */
  headerClassName?: string;
}

interface InternalGlobalHeaderProps {
  toggleExpanded: () => void;
  isExpanded: boolean;
  showNavScreen?: boolean;
}

export type GlobalHeaderMobileProps = Omit<
  GlobalHeaderProps,
  'motifScheme' | 'backgroundColor' | 'localNavDesktop' | 'children'
> &
  InternalGlobalHeaderProps;
export type GlobalHeaderDesktopProps = Omit<
  GlobalHeaderProps,
  'motifScheme' | 'backgroundColor' | 'children'
> &
  InternalGlobalHeaderProps & { navItemAlignment?: GlobalHeaderNavItemAlignment };
